import { useEffect, useRef } from 'react';
import { money } from '../lib/format';
import { currencySymbol, rateOf, rateText, toBase, useRates } from '../lib/currency';

/**
 * حقل سعر الصرف لمستند على خزينة أو حساب بعملة أجنبية.
 *
 * يُملأ بسعر يوم المستند ويبقى قابلًا للتعديل (سعر البنك الفعلي قد يختلف)،
 * ويعرض تحته المقابل بالجنيه للمبلغ المُدخل — وهو ما سيُرحَّل في الدفتر.
 * تغيير التاريخ أو العملة يعيد تعبئته ما لم يعدّله المستخدم بيده.
 */
export function FxRateField({
  id,
  currency,
  date,
  rate,
  onRate,
  fxTotal,
  keepInitial = false,
}: {
  id: string;
  currency: string;
  /** تاريخ المستند `YYYY-MM-DD` — يُؤخذ سعره. */
  date?: string;
  rate: string;
  onRate: (rate: string) => void;
  /** المبلغ بالعملة الذي يُعرض مقابله بالجنيه. */
  fxTotal: number;
  /** مستند محفوظ بسعره: لا يُستبدل سعره بسعر اليوم عند الفتح. */
  keepInitial?: boolean;
}) {
  const rates = useRates(date || undefined);
  const dayRate = rateOf(rates.data, currency);
  const touched = useRef(keepInitial);
  const lastKey = useRef(`${currency}|${date ?? ''}`);

  useEffect(() => {
    const key = `${currency}|${date ?? ''}`;
    if (key !== lastKey.current) {
      lastKey.current = key;
      touched.current = false;
    }
    if (!touched.current && dayRate) onRate(String(dayRate));
    // onRate يتغيّر مع كل رسم؛ التعبئة تتبع السعر والعملة والتاريخ وحدها.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayRate, currency, date]);

  const r = Number(rate);
  const base = r > 0 && fxTotal > 0 ? toBase(fxTotal, r) : null;

  return (
    <div className="field">
      <label className="label req" htmlFor={id}>
        سعر الصرف (ج.م لكل 1 {currencySymbol(currency)})
      </label>
      <input
        id={id}
        className="input num"
        type="number"
        min="0"
        step="any"
        value={rate}
        onChange={(e) => {
          touched.current = true;
          onRate(e.target.value);
        }}
      />
      <span className="tiny mute">
        {dayRate
          ? `سعر يوم ${date || 'اليوم'}: ${rateText(dayRate)}`
          : rates.isPending
            ? 'جارٍ جلب السعر…'
            : 'لا يوجد سعر محفوظ — أدخله يدويًا'}
        {base !== null && <> — المقابل بالجنيه: <b className="num">{money(base)}</b></>}
      </span>
    </div>
  );
}
